/**
 * batch-63 cursusplanning (FO-468) - PURE regels achter de cursusaanvraag.
 *
 * Een cursusaanvraag is het verzoek AAN DE OPLEIDER om een of meer
 * medewerkers op een voorkeursdatum op een gekozen locatie in te plannen (niet
 * te verwarren met de FO-004-`OpleidingAanvraag`, het interne verzoek van een
 * medewerker aan HR). Alles hier is I/O-vrij en daarmee los unit-testbaar; de
 * API en de backoffice-wizard delen deze regels:
 *
 *   - de aanvraagreferentie (`CA-2026-0042`) die in het onderwerp staat en er
 *     bij een reply weer af komt;
 *   - het locatievoorstel: afstand woonadres -> elke actieve opleiderslocatie
 *     (hemelsbreed, `haversineKm` uit targeting-distance - geen tweede
 *     haversine), laagste gemiddelde bij meerdere deelnemers, spreiding en een
 *     splits-voorstel als deelnemers ver uiteen wonen;
 *   - de cursusdagen vanaf de voorkeursdatum (opeenvolgende werkdagen);
 *   - de bewaking: wanneer een herinnering of signaal due is (werkdagen);
 *   - de vergelijking van een bevestiging met wat er gepland staat: elke
 *     afwijking (datum, tijd, locatie) vraagt een menselijk akkoord;
 *   - de "onder voorbehoud"-markering op de voorlopige cursus-werkbon.
 *
 * Datums reizen als NL-kalenderdag `yyyy-MM-dd` (opgeslagen als UTC-middernacht
 * van die dag, zie `dagtaken-datum.ts`).
 */

import { haversineKm, roundKm } from './targeting-distance'
import { emailDomein, PUBLIEKE_MAILDOMEINEN } from './email-domein'
import { isWerkdag } from './uren-calculations'
import { formatDagDatum, parseDagDatum } from './dagtaken-datum'

// ─── Referentie ──────────────────────────────────────────────────────────────

/** Vast voorvoegsel van een cursusaanvraag-referentie. */
export const CURSUS_AANVRAAG_REFERENTIE_PREFIX = 'CA'

/** `CA-2026-0042`: jaar + volgnummer (minimaal 4 cijfers). */
export function formatCursusAanvraagReferentie(jaar: number, volgnummer: number): string {
  return `${CURSUS_AANVRAAG_REFERENTIE_PREFIX}-${jaar}-${String(volgnummer).padStart(4, '0')}`
}

const REFERENTIE_KAAL_RE = /^CA-(\d{4})-(\d{1,6})$/

/**
 * Het volgnummer uit een referentie van het gegeven jaar, of null als de
 * referentie niet bij dat jaar hoort of geen geldig formaat heeft.
 */
export function parseCursusAanvraagVolgnummer(referentie: string, jaar: number): number | null {
  const m = REFERENTIE_KAAL_RE.exec(referentie.trim().toUpperCase())
  if (!m || Number(m[1]) !== jaar) return null
  const n = Number(m[2])
  return Number.isInteger(n) && n > 0 ? n : null
}

/**
 * STRIKTE onderwerp-regex: alleen een `[REF: CA-jjjj-nnnn]`-token. Andere
 * `[REF: ...]`-codes (bv. de FO-412-akkoordmail) matchen bewust niet - liever
 * geen koppeling (en dus de niet-gekoppeld-lijst) dan een verkeerde.
 */
const REFERENTIE_TOKEN_RE = /\[\s*REF\s*:\s*(CA-\d{4}-\d{1,6})\s*\]/i
const REFERENTIE_TOKEN_GLOBAAL_RE = /\s*\[\s*REF\s*:\s*CA-\d{4}-\d{1,6}\s*\]/gi

/** De referentie uit een (reply-)onderwerp, in hoofdletters; null als er geen staat. */
export function parseCursusAanvraagReferentie(onderwerp: string | null | undefined): string | null {
  if (!onderwerp) return null
  const m = REFERENTIE_TOKEN_RE.exec(onderwerp)
  return m?.[1] ? m[1].toUpperCase() : null
}

/** Het token zoals het in het onderwerp staat: `[REF: CA-2026-0042]`. */
export function cursusAanvraagReferentieToken(referentie: string): string {
  return `[REF: ${referentie.trim().toUpperCase()}]`
}

/**
 * Het definitieve onderwerp: de (bewerkbare) tekst plus precies één
 * referentie-token aan het eind. Een token dat de gebruiker zelf liet staan of
 * kopieerde wordt eerst verwijderd, zodat er nooit twee (of een verkeerde)
 * referenties in het onderwerp staan.
 */
export function onderwerpMetReferentie(onderwerp: string, referentie: string): string {
  const kaal = onderwerp.replace(REFERENTIE_TOKEN_GLOBAAL_RE, '').replace(/\s+/g, ' ').trim()
  const token = cursusAanvraagReferentieToken(referentie)
  return kaal === '' ? token : `${kaal} ${token}`
}

// ─── Locatievoorstel ─────────────────────────────────────────────────────────

/**
 * Extra omrijden (km, hemelsbreed) t.o.v. de eigen dichtstbijzijnde locatie
 * waarboven een groepsvoorstel "ver uiteen" heet en de wizard een splitsing
 * voorstelt.
 */
export const CURSUS_SPLITS_DREMPEL_KM = 30

export interface CursusDeelnemerHerkomst {
  employeeId: string
  naam: string
  /** Gegeocodeerd woonadres; null = onbekend (afstand "onbekend"). */
  latitude: number | null
  longitude: number | null
}

export interface CursusLocatieKandidaat {
  locatieId: string
  naam: string
  plaats: string | null
  latitude: number | null
  longitude: number | null
  isPrimair: boolean
  /** Default-locatie van de gekozen opleiding (tie-breaker + terugval). */
  isOpleidingDefault: boolean
}

export interface CursusLocatieAfstand {
  employeeId: string
  /** Hemelsbreed, 1 decimaal; null = onbekend (geen coördinaten aan een van beide kanten). */
  afstandKm: number | null
}

export interface CursusLocatieRij {
  locatieId: string
  naam: string
  plaats: string | null
  heeftCoordinaten: boolean
  isPrimair: boolean
  isOpleidingDefault: boolean
  afstanden: CursusLocatieAfstand[]
  /** Gemiddelde over de deelnemers MET bekende afstand; null als die er niet zijn. */
  gemiddeldeKm: number | null
  maxKm: number | null
  aantalBekend: number
}

export interface CursusSplitsGroep {
  locatieId: string
  employeeIds: string[]
  gemiddeldeKm: number | null
}

export type CursusLocatieAanbevelingBron =
  | 'AFSTAND'
  | 'OPLEIDING_DEFAULT'
  | 'PRIMAIRE_LOCATIE'
  | 'ENIGE_LOCATIE'
  | 'GEEN'

export interface CursusLocatieVoorstel {
  /** Bekende gemiddelden oplopend, daarna de locaties zonder bruikbare afstand. */
  rijen: CursusLocatieRij[]
  aanbevolenLocatieId: string | null
  aanbevelingBron: CursusLocatieAanbevelingBron
  /** Deelnemers zonder gegeocodeerd woonadres (afstand "onbekend"). */
  deelnemersZonderHerkomst: string[]
  /** Locaties zonder coördinaten (melding + link naar opleider-beheer). */
  locatiesZonderCoordinaten: string[]
  /** Grootste onderlinge (hemelsbrede) afstand tussen de woonadressen. */
  spreidingKm: number | null
  /** Het groepsvoorstel kost minstens één deelnemer meer dan de drempel extra. */
  verUiteen: boolean
  /** Groepen per eigen dichtstbijzijnde locatie; alleen bij `verUiteen`. */
  splitsVoorstel: CursusSplitsGroep[] | null
}

function heeftCoords<T extends { latitude: number | null; longitude: number | null }>(
  p: T,
): p is T & { latitude: number; longitude: number } {
  return (
    typeof p.latitude === 'number' &&
    typeof p.longitude === 'number' &&
    Number.isFinite(p.latitude) &&
    Number.isFinite(p.longitude)
  )
}

function gemiddelde(waarden: number[]): number | null {
  if (waarden.length === 0) return null
  return roundKm(waarden.reduce((s, v) => s + v, 0) / waarden.length)
}

/** Sorteersleutel voor gelijke afstanden: opleiding-default, primair, naam. */
function tieBreak(a: CursusLocatieRij, b: CursusLocatieRij): number {
  if (a.isOpleidingDefault !== b.isOpleidingDefault) return a.isOpleidingDefault ? -1 : 1
  if (a.isPrimair !== b.isPrimair) return a.isPrimair ? -1 : 1
  return a.naam.localeCompare(b.naam, 'nl')
}

/**
 * Stelt de locatie voor met de laagste gemiddelde afstand over de deelnemers
 * met een bekend woonadres. Nooit gokken: zonder bruikbare afstanden valt het
 * voorstel terug op de opleiding-default of de primaire locatie (en zegt dat),
 * nooit op een willekeurige.
 */
export function berekenCursusLocatieVoorstel(
  deelnemers: readonly CursusDeelnemerHerkomst[],
  locaties: readonly CursusLocatieKandidaat[],
  opties: { splitsDrempelKm?: number } = {},
): CursusLocatieVoorstel {
  const drempel = opties.splitsDrempelKm ?? CURSUS_SPLITS_DREMPEL_KM
  const bekend = deelnemers.filter(heeftCoords)

  const rijen: CursusLocatieRij[] = locaties.map((l) => {
    const locCoords = heeftCoords(l)
    const afstanden: CursusLocatieAfstand[] = deelnemers.map((d) => ({
      employeeId: d.employeeId,
      afstandKm:
        locCoords && heeftCoords(d)
          ? roundKm(haversineKm(d.latitude, d.longitude, l.latitude, l.longitude))
          : null,
    }))
    const km = afstanden.map((a) => a.afstandKm).filter((v): v is number => v !== null)
    return {
      locatieId: l.locatieId,
      naam: l.naam,
      plaats: l.plaats,
      heeftCoordinaten: locCoords,
      isPrimair: l.isPrimair,
      isOpleidingDefault: l.isOpleidingDefault,
      afstanden,
      gemiddeldeKm: gemiddelde(km),
      maxKm: km.length > 0 ? Math.max(...km) : null,
      aantalBekend: km.length,
    }
  })

  rijen.sort((a, b) => {
    if (a.gemiddeldeKm !== null && b.gemiddeldeKm !== null) {
      if (a.gemiddeldeKm !== b.gemiddeldeKm) return a.gemiddeldeKm - b.gemiddeldeKm
      if ((a.maxKm ?? 0) !== (b.maxKm ?? 0)) return (a.maxKm ?? 0) - (b.maxKm ?? 0)
      return tieBreak(a, b)
    }
    if (a.gemiddeldeKm !== null) return -1
    if (b.gemiddeldeKm !== null) return 1
    return tieBreak(a, b)
  })

  let aanbevolen: CursusLocatieRij | null = rijen.find((r) => r.gemiddeldeKm !== null) ?? null
  let bron: CursusLocatieAanbevelingBron = aanbevolen ? 'AFSTAND' : 'GEEN'
  if (!aanbevolen) {
    const viaDefault = rijen.find((r) => r.isOpleidingDefault)
    const viaPrimair = rijen.find((r) => r.isPrimair)
    if (viaDefault) {
      aanbevolen = viaDefault
      bron = 'OPLEIDING_DEFAULT'
    } else if (viaPrimair) {
      aanbevolen = viaPrimair
      bron = 'PRIMAIRE_LOCATIE'
    } else if (rijen.length === 1) {
      aanbevolen = rijen[0] ?? null
      bron = 'ENIGE_LOCATIE'
    }
  }

  // Spreiding: grootste onderlinge afstand tussen de bekende woonadressen.
  let spreiding: number | null = null
  for (let i = 0; i < bekend.length; i++) {
    for (let j = i + 1; j < bekend.length; j++) {
      const a = bekend[i]!
      const b = bekend[j]!
      const km = roundKm(haversineKm(a.latitude, a.longitude, b.latitude, b.longitude))
      spreiding = spreiding === null ? km : Math.max(spreiding, km)
    }
  }

  // Ver uiteen + splitsen: alleen zinvol bij een afstands-aanbeveling en >= 2
  // bekende deelnemers. Per deelnemer zijn eigen dichtstbijzijnde locatie (in
  // de gesorteerde volgorde, zodat gelijke afstanden dezelfde tie-break volgen).
  let verUiteen = false
  let splitsVoorstel: CursusSplitsGroep[] | null = null
  if (aanbevolen && bron === 'AFSTAND' && bekend.length >= 2) {
    const eigenLocatie = new Map<string, string>()
    for (const d of bekend) {
      let beste: { locatieId: string; km: number } | null = null
      for (const r of rijen) {
        const km = r.afstanden.find((a) => a.employeeId === d.employeeId)?.afstandKm ?? null
        if (km === null) continue
        if (beste === null || km < beste.km) beste = { locatieId: r.locatieId, km }
      }
      if (!beste) continue
      eigenLocatie.set(d.employeeId, beste.locatieId)
      const viaGroep =
        aanbevolen.afstanden.find((a) => a.employeeId === d.employeeId)?.afstandKm ?? null
      if (viaGroep !== null && viaGroep - beste.km > drempel) verUiteen = true
    }

    if (verUiteen) {
      const groepen = new Map<string, string[]>()
      for (const d of deelnemers) {
        // Deelnemers zonder bekende afstand gaan mee met het groepsvoorstel.
        const locId = eigenLocatie.get(d.employeeId) ?? aanbevolen.locatieId
        const lijst = groepen.get(locId) ?? []
        lijst.push(d.employeeId)
        groepen.set(locId, lijst)
      }
      if (groepen.size >= 2) {
        splitsVoorstel = rijen
          .filter((r) => groepen.has(r.locatieId))
          .map((r) => {
            const ids = groepen.get(r.locatieId) ?? []
            const km = r.afstanden
              .filter((a) => ids.includes(a.employeeId))
              .map((a) => a.afstandKm)
              .filter((v): v is number => v !== null)
            return { locatieId: r.locatieId, employeeIds: ids, gemiddeldeKm: gemiddelde(km) }
          })
      } else {
        verUiteen = false
      }
    }
  }

  return {
    rijen,
    aanbevolenLocatieId: aanbevolen?.locatieId ?? null,
    aanbevelingBron: aanbevolen ? bron : 'GEEN',
    deelnemersZonderHerkomst: deelnemers.filter((d) => !heeftCoords(d)).map((d) => d.employeeId),
    locatiesZonderCoordinaten: locaties.filter((l) => !heeftCoords(l)).map((l) => l.locatieId),
    spreidingKm: spreiding,
    verUiteen,
    splitsVoorstel,
  }
}

// ─── Cursusdagen ─────────────────────────────────────────────────────────────

/** Maximaal aantal dagen dat de wizard voor één aanvraag inplant. */
export const CURSUS_AANVRAAG_MAX_DAGEN = 20

/**
 * De cursusdagen vanaf de voorkeursdatum: de gekozen startdag zelf (ook als dat
 * een zaterdag is - sommige opleiders geven dan les) plus de eerstvolgende
 * werkdagen tot het aantal dagen bereikt is.
 */
export function bepaalCursusDagen(
  startDag: string,
  aantalDagen: number,
  feestdagen: readonly Date[] = [],
): string[] {
  const n = Math.max(1, Math.min(CURSUS_AANVRAAG_MAX_DAGEN, Math.floor(aantalDagen) || 1))
  const start = parseDagDatum(startDag)
  const dagen = [formatDagDatum(start)]
  const cursor = new Date(start.getTime())
  let veiligheid = 0
  while (dagen.length < n && veiligheid < 400) {
    cursor.setUTCDate(cursor.getUTCDate() + 1)
    veiligheid += 1
    if (isWerkdag(cursor, [...feestdagen])) dagen.push(formatDagDatum(cursor))
  }
  return dagen
}

/**
 * Een door de planner (of opleider) opgegeven dagenlijst: geldige
 * kalenderdagen, uniek en oplopend. Ongeldige waarden vallen weg.
 */
export function normaliseerCursusDagen(dagen: readonly string[]): string[] {
  const geldig = new Set<string>()
  for (const d of dagen) {
    const s = (d ?? '').trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) continue
    try {
      if (formatDagDatum(parseDagDatum(s)) === s) geldig.add(s)
    } catch {
      // geen echte kalenderdag
    }
  }
  return [...geldig].sort()
}

// ─── Bewaking ────────────────────────────────────────────────────────────────

/** Default: geen antwoord na zoveel werkdagen -> herinnering. */
export const CURSUS_AANVRAAG_HERINNERING_WERKDAGEN = 3
/** Default: maximaal zoveel automatische herinneringen aan de opleider. */
export const CURSUS_AANVRAAG_MAX_HERINNERINGEN = 2

/** De NL-kalenderdag waarop `werkdagen` werkdagen na `vanafDag` verstreken zijn. */
export function werkdagenNa(
  vanafDag: string,
  werkdagen: number,
  feestdagen: readonly Date[] = [],
): string {
  const cursor = parseDagDatum(vanafDag)
  let geteld = 0
  let veiligheid = 0
  const doel = Math.max(0, Math.floor(werkdagen))
  while (geteld < doel && veiligheid < 400) {
    cursor.setUTCDate(cursor.getUTCDate() + 1)
    veiligheid += 1
    if (isWerkdag(cursor, [...feestdagen])) geteld += 1
  }
  return formatDagDatum(cursor)
}

/** De NL-kalenderdag `werkdagen` werkdagen vóór `dag` (0 = de dag zelf). */
export function werkdagenVoor(
  dag: string,
  werkdagen: number,
  feestdagen: readonly Date[] = [],
): string {
  const cursor = parseDagDatum(dag)
  let geteld = 0
  let veiligheid = 0
  const doel = Math.max(0, Math.floor(werkdagen))
  while (geteld < doel && veiligheid < 400) {
    cursor.setUTCDate(cursor.getUTCDate() - 1)
    veiligheid += 1
    if (isWerkdag(cursor, [...feestdagen])) geteld += 1
  }
  return formatDagDatum(cursor)
}

export type CursusAanvraagKanaalSleutel = 'MAIL' | 'HANDMATIG' | 'INTERN'

export interface HerinneringInvoer {
  /** Alleen een aanvraag die op het antwoord van de opleider wacht telt. */
  wachtOpAntwoord: boolean
  kanaal: CursusAanvraagKanaalSleutel
  /** Laatste contactmoment met de opleider (verzonden of laatste herinnering). */
  laatsteContactDag: string
  herinneringenVerzonden: number
  /** Staat het definitieve "geen antwoord"-signaal al uit? */
  geenAntwoordGemeld: boolean
  vandaag: string
  werkdagen: number
  maxHerinneringen: number
  feestdagen?: readonly Date[]
}

export type HerinneringActie =
  /** Herinneringsmail aan de opleider + signaal aan de aanvrager. */
  | 'HERINNERING_MAIL'
  /** Handmatig kanaal (geen mailadres): alleen een signaal aan de aanvrager. */
  | 'SIGNAAL'
  /** Maximum bereikt en weer geen antwoord: eenmalig definitief signaal. */
  | 'GEEN_ANTWOORD'
  | 'GEEN'

export interface HerinneringBesluit {
  actie: HerinneringActie
  /** De dag waarop de volgende stap due is/was (null als er niets meer komt). */
  dueDag: string | null
}

export function bepaalHerinneringActie(i: HerinneringInvoer): HerinneringBesluit {
  if (!i.wachtOpAntwoord || i.kanaal === 'INTERN' || i.geenAntwoordGemeld) {
    return { actie: 'GEEN', dueDag: null }
  }
  const dueDag = werkdagenNa(i.laatsteContactDag, i.werkdagen, i.feestdagen ?? [])
  if (i.vandaag < dueDag) return { actie: 'GEEN', dueDag }
  if (i.herinneringenVerzonden >= i.maxHerinneringen) return { actie: 'GEEN_ANTWOORD', dueDag }
  return { actie: i.kanaal === 'MAIL' ? 'HERINNERING_MAIL' : 'SIGNAAL', dueDag }
}

/**
 * De geplande, nog niet bevestigde cursusdag nadert (of is voorbij): het signaal
 * gaat `werkdagenVooraf` werkdagen vóór die dag uit, zodat de planner nog kan
 * bellen of omplannen voordat een monteur voor een dichte deur staat.
 */
export function isVoorkeursdatumVerstreken(i: {
  wachtOpBevestiging: boolean
  geplandeDag: string | null
  vandaag: string
  /** Standaard 0 = pas op de dag zelf. */
  werkdagenVooraf?: number
  feestdagen?: readonly Date[]
}): boolean {
  if (!i.wachtOpBevestiging || i.geplandeDag === null) return false
  const signaalDag = werkdagenVoor(i.geplandeDag, i.werkdagenVooraf ?? 0, i.feestdagen ?? [])
  return i.vandaag >= signaalDag
}

// ─── Bevestiging vergelijken ─────────────────────────────────────────────────

export type CursusAfwijking = 'DATUM' | 'TIJD' | 'LOCATIE'

export interface CursusGeplandKern {
  /** Alle geplande cursusdagen (yyyy-MM-dd), minimaal één. */
  dagen: readonly string[]
  startTijd: string | null
  eindTijd: string | null
  locatieId: string | null
}

export interface CursusBevestigdKern {
  /** Alle door de opleider genoemde cursusdagen (leeg = geen bruikbare datum). */
  dagen: readonly string[]
  startTijd: string | null
  eindTijd: string | null
  /** Door de opleider genoemde locatie, al herleid naar een locatie van deze opleider (of null). */
  locatieId: string | null
  /** Er stond wel een locatie in de reply, maar die is niet eenduidig herleidbaar. */
  locatieOnherleidbaar: boolean
}

export interface BevestigingVergelijking {
  afwijkingen: CursusAfwijking[]
  /** De reply noemt geen (bruikbare) datum. */
  datumOntbreekt: boolean
  /**
   * Meerdaagse cursus waarvan de reply maar een deel van de geplande dagen
   * noemt: niet af te leiden of de rest klopt, dus een mens beslist.
   */
  dagenOnvolledig: boolean
  /** Mag zonder mens definitief: alle dagen gelijk en verder niets afwijkends. */
  automatischDefinitief: boolean
}

/**
 * Vergelijk de bevestiging met de planning. Alleen een bevestiging van precies
 * de geplande dag(en), zonder afwijkende tijd of locatie, mag automatisch
 * definitief; al het andere vraagt een menselijk akkoord:
 *   - een andere dag (ook één van meerdere) is een DATUM-afwijking;
 *   - bij een meerdaagse cursus moet de opleider alle dagen noemen;
 *   - tijden die de opleider noemt terwijl de aanvraag er geen had, zijn een
 *     TIJD-afwijking (ze worden pas na akkoord overgenomen).
 */
export function vergelijkBevestiging(
  gepland: CursusGeplandKern,
  bevestigd: CursusBevestigdKern,
): BevestigingVergelijking {
  const geplandeDagen = normaliseerCursusDagen(gepland.dagen)
  const genoemd = normaliseerCursusDagen(bevestigd.dagen)
  const afwijkingen: CursusAfwijking[] = []
  const geplandSet = new Set(geplandeDagen)
  const buitenPlanning = genoemd.some((d) => !geplandSet.has(d))
  if (buitenPlanning) afwijkingen.push('DATUM')
  const datumOntbreekt = genoemd.length === 0
  const dagenOnvolledig = !datumOntbreekt && !buitenPlanning && genoemd.length < geplandeDagen.length

  const tijdAnders = (genoemdeTijd: string | null, geplandeTijd: string | null): boolean => {
    const g = normaliseerTijd(genoemdeTijd)
    if (g === null) return false
    return g !== normaliseerTijd(geplandeTijd)
  }
  if (tijdAnders(bevestigd.startTijd, gepland.startTijd) || tijdAnders(bevestigd.eindTijd, gepland.eindTijd)) {
    afwijkingen.push('TIJD')
  }
  const locatieAnders =
    bevestigd.locatieOnherleidbaar ||
    (bevestigd.locatieId !== null && bevestigd.locatieId !== gepland.locatieId)
  if (locatieAnders) afwijkingen.push('LOCATIE')
  return {
    afwijkingen,
    datumOntbreekt,
    dagenOnvolledig,
    automatischDefinitief: afwijkingen.length === 0 && !datumOntbreekt && !dagenOnvolledig,
  }
}

const TIJD_RE = /^(\d{1,2})[:.](\d{2})$/

/** `9:00`, `09.00` -> `09:00`; null bij een onbruikbare waarde. */
export function normaliseerTijd(tijd: string | null | undefined): string | null {
  const m = TIJD_RE.exec((tijd ?? '').trim())
  if (!m) return null
  const u = Number(m[1])
  const min = Number(m[2])
  if (u > 23 || min > 59) return null
  return `${String(u).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

export interface LocatieTekstKandidaat {
  locatieId: string
  naam: string
  plaats: string | null
  straat: string | null
}

function normaliseerTekst(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Herleid een vrije locatietekst uit de reply ("Eindhoven", "vestiging Best")
 * naar precies één locatie van de opleider. Twee of meer kandidaten of geen
 * enkele = null (dan beslist de planner).
 */
export function herleidLocatieTekst(
  tekst: string | null | undefined,
  locaties: readonly LocatieTekstKandidaat[],
): string | null {
  const t = normaliseerTekst(tekst ?? '')
  if (t === '') return null
  const treffers = locaties.filter((l) => {
    const naam = normaliseerTekst(l.naam)
    const plaats = normaliseerTekst(l.plaats ?? '')
    const straat = normaliseerTekst(l.straat ?? '')
    const bevat = (hooiberg: string, naald: string): boolean =>
      naald.length >= 3 && ` ${hooiberg} `.includes(` ${naald} `)
    return (
      (plaats !== '' && (bevat(t, plaats) || t === plaats)) ||
      (naam !== '' && (bevat(t, naam) || bevat(naam, t))) ||
      (straat !== '' && bevat(t, straat))
    )
  })
  return treffers.length === 1 ? (treffers[0]?.locatieId ?? null) : null
}

/**
 * Een door de AI teruggegeven datum is alleen bruikbaar als hij een echte
 * kalenderdag is binnen een redelijk venster rond vandaag (niet in het
 * verleden van meer dan een week, niet verder dan twee jaar vooruit).
 */
export function bruikbareReplyDatum(waarde: string | null | undefined, vandaag: string): string | null {
  const s = (waarde ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  let dag: Date
  try {
    dag = parseDagDatum(s)
  } catch {
    return null
  }
  const nu = parseDagDatum(vandaag)
  const min = nu.getTime() - 7 * 86_400_000
  const max = nu.getTime() + 2 * 366 * 86_400_000
  if (dag.getTime() < min || dag.getTime() > max) return null
  return formatDagDatum(dag)
}

// ─── Afzender ────────────────────────────────────────────────────────────────

/** Het (lowercase) domein van een mailadres, of null. */
/**
 * Hoort dit afzenderadres bij de opleider? Exact een bekend adres, of hetzelfde
 * (niet-publieke) domein - opleiders antwoorden vaak vanaf een persoonlijk adres
 * van hetzelfde bedrijf (planning@ i.p.v. info@). Een ander adres wordt WEL
 * verwerkt, maar altijd als review-item.
 */
export function isBekendeOpleiderAfzender(
  afzenderAdres: string | null | undefined,
  bekendeAdressen: Array<string | null | undefined>,
): boolean {
  const adres = (afzenderAdres ?? '').trim().toLowerCase()
  if (adres === '') return false
  const bekend = bekendeAdressen.map((a) => (a ?? '').trim().toLowerCase()).filter((a) => a !== '')
  if (bekend.includes(adres)) return true
  const domein = emailDomein(adres)
  if (!domein || PUBLIEKE_MAILDOMEINEN.has(domein)) return false
  return bekend.some((a) => emailDomein(a) === domein)
}

// ─── Werkbon-naam: onder voorbehoud ──────────────────────────────────────────

/**
 * Zichtbare markering op de voorlopige cursus-werkbon (planbord, werkbonlijst,
 * portal). Bewust een VOORVOEGSEL: het planbord kapt de naam af, en juist dit
 * stuk moet de planner zien. Verdwijnt zodra de opleider heeft bevestigd.
 */
export const ONDER_VOORBEHOUD_PREFIX = 'Onder voorbehoud - '

export function isOnderVoorbehoudNaam(naam: string | null | undefined): boolean {
  return (naam ?? '').startsWith(ONDER_VOORBEHOUD_PREFIX)
}

/** Zet of verwijder de markering (idempotent). */
export function metOnderVoorbehoud(naam: string, onderVoorbehoud: boolean): string {
  const kaal = isOnderVoorbehoudNaam(naam) ? naam.slice(ONDER_VOORBEHOUD_PREFIX.length) : naam
  return onderVoorbehoud ? `${ONDER_VOORBEHOUD_PREFIX}${kaal}` : kaal
}

/**
 * De naam die een bestaande cursus-werkbon krijgt na (her)generatie.
 *
 * - Een door STREEP gegenereerde naam ("<locatie> - <opleiding>" of alleen de
 *   opleiding) volgt de nieuwe gegenereerde naam (bijvoorbeeld na een andere
 *   locatie).
 * - Een door de planner aangepaste naam blijft staan; alleen het voorvoegsel
 *   "Onder voorbehoud - " wisselt mee met de status.
 * - Een achtervoegsel (vervolg-bon) blijft altijd staan.
 * Geeft null als er niets hoeft te veranderen.
 */
export function herzieVoorbehoudNaam(
  huidigeNaam: string,
  opts: { gegenereerdeNaam: string; opleidingNaam: string; onderVoorbehoud: boolean; achtervoegsel?: string },
): string | null {
  const kaal = metOnderVoorbehoud(huidigeNaam, false)
  const sfx = opts.achtervoegsel && kaal.endsWith(opts.achtervoegsel) ? opts.achtervoegsel : ''
  const kern = sfx ? kaal.slice(0, kaal.length - sfx.length) : kaal
  const gegenereerd = kern === opts.opleidingNaam || kern.endsWith(` - ${opts.opleidingNaam}`)
  const basis = gegenereerd ? metOnderVoorbehoud(opts.gegenereerdeNaam, false) : kern
  const doel = metOnderVoorbehoud(`${basis}${sfx}`, opts.onderVoorbehoud)
  return doel === huidigeNaam ? null : doel
}
